import { Address, AddressInput, Profile, ProfileUpdate } from '@teko/user';
import { Field, FormikProps } from 'formik';
import { t } from 'i18n';
import { InputField } from './types';
import { CustomerType } from '@teko/ui-user';
import { Select, DateTimePicker, styled } from '@teko/ds-desktop';
import { StyledRadio } from './styles';
import { Area, Location } from '@teko/location';
import { format } from 'date-fns';
import { DateFormat } from 'types';

export const getAddressString = (address: Address | null): string => {
  return [
    address?.address,
    address?.wardName,
    address?.districtName,
    address?.provinceName,
  ]
    .filter(Boolean)
    .join(', ');
};

export function fallbackTransform<T>(
  transformFunc: ((original: T) => T) | undefined,
  original: T,
): T {
  if (!!transformFunc) return transformFunc(original);
  else return original;
}

export const getProfileInputFields = (
  values: Partial<ProfileUpdate>,
  setFieldValue: FormikProps<Partial<ProfileUpdate>>['setFieldValue'],
  profile: Profile | null,
  partner: string | undefined,
  customerTypes: CustomerType[],
  notEditCustomerType: boolean | undefined,
  allowEdit: boolean,
): InputField<ProfileUpdate>[] => {
  return [
    {
      label: t('Họ tên'),
      name: 'name',
    },
    {
      label: t('Email'),
      name: 'email',
    },
    {
      label: t('Số điện thoại'),
      name: 'telephone',
      disabled: !!partner || !!profile?.telephone,
    },
    {
      type: 'date',
      label: t('Ngày sinh'),
      name: 'dob',
      children: (
        <StyledDate
          type="date"
          onChange={(value) => {
            setFieldValue(
              'dob',
              value && format(new Date(value), DateFormat.YMD),
            );
          }}
          value={values.dob ? new Date(values.dob) : undefined}
        />
      ),
    },
    {
      condition: !!customerTypes?.length,
      label: t('Loại khách hàng'),
      name: 'customerTypeId',
      children: (
        <Select
          disabled={
            notEditCustomerType || (!!profile?.customerTypeId && !allowEdit)
          }
          options={customerTypes}
          selectValue={(value: string | number | null) =>
            // UX4BUYER-4531
            setFieldValue('customerTypeId', value)
          }
          /* this selector is only rendered if customerTypes not empty
               so fallback value of 0 will not affect. */
          value={(values.customerTypeId || customerTypes?.[0]?.value) ?? 0}
        />
      ),
    },
    {
      label: t('Giới tính'),
      name: 'sex',
      children: [
        {
          label: t('Nam'),
          value: 'M',
        },
        {
          label: t('Nữ'),
          value: 'F',
        },
        {
          label: t('Khác'),
          value: 'O',
        },
      ].map(({ label, value }) => (
        <StyledRadio key={value}>
          <Field
            type="radio"
            name="sex"
            value={value}
            disabled={!!profile?.sex && !allowEdit}
          />
          {label}
        </StyledRadio>
      )),
    },
  ].map((field) => ({
    disabled: !!profile?.[field.name as keyof Profile] && !allowEdit,
    ...field,
    name: field.name as keyof ProfileUpdate,
  })) as InputField<ProfileUpdate>[];
};

export const getAddressInputFields = (
  values: Partial<AddressInput>,
  setFieldValue: FormikProps<Partial<AddressInput>>['setFieldValue'],
  provinces: Location[],
  districts: Location[],
  wards: Location[],
  onProvinceChanged: (code: Location['code']) => Promise<void>,
  onDistrictChanged: (code: Location['code']) => Promise<void>,
  onWardChanged: (code: Location['code']) => void,
) =>
  [
    {
      label: t('Tỉnh/Thành phố'),
      name: 'provinceCode',
      children: (
        <Select
          options={provinces.map((item) => ({
            value: item.code,
            text: item.name,
          }))}
          selectValue={(code: string | number) => {
            setFieldValue('provinceCode', code);
            onProvinceChanged(code.toString());
          }}
          value={values.provinceCode || ''}
        />
      ),
    },
    {
      label: t('Quận/Huyện'),
      name: 'districtCode',
      children: (
        <Select
          options={districts.map((item) => ({
            value: item.code,
            text: item.name,
          }))}
          selectValue={(code: string | number) => {
            setFieldValue('districtCode', code);
            onDistrictChanged(code.toString());
          }}
          value={values.districtCode || ''}
        />
      ),
    },
    {
      label: t('Phường/Xã'),
      name: 'wardCode',
      children: (
        <Select
          options={wards.map((item) => ({
            value: item.code,
            text: item.name,
          }))}
          selectValue={(code: string | number) => {
            setFieldValue('wardCode', code);
            onWardChanged(code.toString());
          }}
          value={values.wardCode || ''}
        />
      ),
    },
    {
      label: t('Địa chỉ cụ thể (Số nhà, tên đường...)'),
      name: 'address',
    },
  ] as InputField<AddressInput>[];

export const getAddressInputFieldsV2 = (
  values: Partial<Omit<AddressInput, 'districtCode'>>,
  setFieldValue: FormikProps<
    Partial<Omit<AddressInput, 'districtCode'>>
  >['setFieldValue'],
  provinces: Area[],
  wards: Area[],
  onProvinceChanged: (code: Area['code']) => void,
  onWardChanged: (code: Area['code']) => void,
) =>
  [
    {
      label: t('Tỉnh/Thành phố'),
      name: 'provinceCode',
      children: (
        <Select
          options={provinces.map((item) => ({
            value: item.code,
            text: item.title,
          }))}
          selectValue={(value) => {
            const location = provinces.find((prov) => prov.code === value);
            if (location) {
              setFieldValue('provinceCode', location.code);
              onProvinceChanged(location.code);
            }
          }}
          value={values.provinceCode || ''}
        />
      ),
    },
    {
      label: t('Phường/Xã'),
      name: 'wardCode',
      children: (
        <Select
          options={wards.map((item) => ({
            value: item.code,
            text: item.title,
          }))}
          selectValue={(value) => {
            const location = wards.find((ward) => ward.code === value);
            if (location) {
              setFieldValue('wardCode', location.code);
              onWardChanged(location.code);
            }
          }}
          value={values.wardCode || ''}
        />
      ),
    },
    {
      label: t('Địa chỉ cụ thể (Số nhà, tên đường...)'),
      name: 'address',
    },
  ] as InputField<AddressInput>[];

const StyledDate = styled(DateTimePicker)`
  width: 100%;
  .rc-picker {
    width: 100%;
  }
`;
